import { useEffect, useState } from "react";
import { api, formatTRY, formatApiErrorDetail, toNum, todayISO } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { HeartHandshake, Plus, Pencil, Trash2, Users, Utensils, Wallet, AlertCircle } from "lucide-react";
import { MemberCard } from "@/components/club/MemberCard";
import { useAuth } from "@/context/AuthContext";
import { FinanceDetailDialog } from "@/components/FinanceDetailDialog";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
const EMPTY = { name: "", phone: "", email: "", note: "", active: true };

export default function Club() {
  const [members, setMembers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [memberDlg, setMemberDlg] = useState(null);
  const [pkgDlg, setPkgDlg] = useState(null);
  const [payDlg, setPayDlg] = useState(null);
  const [mealProduct, setMealProduct] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [detail, setDetail] = useState(null);
  const { user } = useAuth();

  const load = async () => {
    const [m, s, mp] = await Promise.all([api.get("/club/members"), api.get("/club/summary"), api.get("/club/meal-product")]);
    setMembers(m.data); setSummary(s.data); setMealProduct(mp.data);
  };
  useEffect(() => { load(); if (user?.role === "admin") api.get("/recipes").then((r) => setRecipes(r.data.filter((x) => !x.template_id))); }, [user?.role]);

  const saveMealProduct = async (v) => {
    try {
      await api.put("/club/meal-product", { recipe_id: v === "none" ? null : v });
      toast.success("Öğün ürünü kaydedildi"); load();
    } catch (e) { err(e); }
  };

  const run = async (fn, msg) => { try { const r = await fn(); toast.success(typeof msg === "function" ? msg(r) : msg); load(); return true; } catch (e) { err(e); return false; } };

  const saveMember = async () => {
    const { id, ...body } = memberDlg;
    if (!body.name.trim()) return toast.error("Ad zorunlu");
    const ok = await run(() => (id ? api.put(`/club/members/${id}`, body) : api.post("/club/members", body)), id ? "Üye güncellendi" : "Üye eklendi");
    if (ok) setMemberDlg(null);
  };
  const savePkg = async () => {
    const ok = await run(() => api.post(`/club/members/${pkgDlg.memberId}/packages`, { meals: toNum(pkgDlg.meals), price: toNum(pkgDlg.price), note: pkgDlg.note }), "Paket tanımlandı");
    if (ok) setPkgDlg(null);
  };
  const savePay = async () => {
    if (!(toNum(payDlg.amount) > 0)) return toast.error("Tutar girin");
    if (toNum(payDlg.commission_amount) > toNum(payDlg.amount)) return toast.error("Sahip payı tahsilat tutarını aşamaz");
    const ok = await run(() => api.post(`/club/members/${payDlg.memberId}/packages/${payDlg.pkgId}/payments`, { amount: toNum(payDlg.amount), payment_type: payDlg.payment_type, date: payDlg.date, note: payDlg.note, commission_amount: toNum(payDlg.commission_amount), owner_name: payDlg.owner_name }), "Tahsilat kaydedildi");
    if (ok) setPayDlg(null);
  };

  const filtered = members.filter((m) => !q || m.name.toLowerCase().includes(q.toLowerCase()) || m.phone.includes(q));

  return (
    <div className="space-y-6" data-testid="club-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Beslenme Kulübü</h1>
          <p className="text-sm text-muted-foreground">Üyeler, öğün hakları ve tahsilat takibi</p>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Üye ara…" value={q} onChange={(e) => setQ(e.target.value)} className="w-48" data-testid="club-search" />
          <Button onClick={() => setMemberDlg({ ...EMPTY })} data-testid="club-add-member"><Plus className="mr-1.5 h-4 w-4" /> Yeni Üye</Button>
        </div>
      </div>

      {summary && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="club-summary">
          <Stat icon={Users} label="Aktif Üye" value={`${summary.active} / ${summary.members}`} />
          <Stat icon={Utensils} label="Toplam Kalan Öğün" value={summary.meals_left} />
          <Stat icon={Wallet} label="Toplam Tahsilat" value={formatTRY(summary.total_paid)} />
          <Stat icon={AlertCircle} label="Bekleyen Bakiye" value={formatTRY(summary.balance)} sub={`${summary.debtors} borçlu üye`} warn={summary.balance > 0} />
        </div>
      )}

      {summary && (
        <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 p-4" data-testid="club-finance">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">Kulüp Kasası — Fit House kasasından ayrı</p>
              <p className="mt-1 text-sm">
                Tahsilat <button type="button" className="font-bold underline decoration-dotted underline-offset-4 hover:text-primary" data-testid="club-fin-income" onClick={() => setDetail({ kind: "club_income", title: "Kulüp Tahsilat Kayıtları" })}>{formatTRY(summary.club_income)}</button>
                {" · "}Kulüp sahibi payı <button type="button" className="font-bold text-destructive underline decoration-dotted underline-offset-4 hover:opacity-80" data-testid="club-fin-commission" onClick={() => setDetail({ kind: "club_expense", title: "Kulüp Sahibi Payı Kayıtları" })}>−{formatTRY(summary.club_commission)}</button>
                {" · "}Kulüp net <b className="text-primary" data-testid="club-fin-net">{formatTRY(summary.club_net)}</b>
              </p>
            </div>
            <p className="text-xs text-muted-foreground">Tutarlara tıklayarak kayıtları görebilirsiniz · Sahibi payı her tahsilatta ₺ tutar olarak girilir</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm" data-testid="club-meal-product">
        <Utensils className="h-4 w-4 text-primary" />
        <span className="text-muted-foreground">Her öğün kullanımında stoktan düşülecek ürün:</span>
        {user?.role === "admin" ? (
          <Select value={mealProduct?.recipe_id || "none"} onValueChange={saveMealProduct}>
            <SelectTrigger className="w-72" data-testid="meal-product-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Otomatik ("Beslenme Kulübü…" adlı ürün)</SelectItem>
              {recipes.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`meal-product-${r.id}`}>{r.display_name || r.product_name}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : null}
        <span className={`font-semibold ${mealProduct?.resolved ? "text-primary" : "text-destructive"}`} data-testid="meal-product-resolved">
          {mealProduct?.resolved ? `${mealProduct.resolved}${mealProduct.resolved_size ? ` (${mealProduct.resolved_size})` : ""}` : "Tanımlı değil — öğün kullanımı stok düşmez"}
        </span>
      </div>

      <div className="rounded-xl border border-border bg-card" data-testid="club-members">
        {filtered.length === 0 && <p className="flex items-center gap-2 p-8 text-sm text-muted-foreground"><HeartHandshake className="h-4 w-4" /> Henüz üye yok</p>}
        {filtered.map((m) => (
          <MemberCard key={m.id} m={m}
            onEdit={() => setMemberDlg({ id: m.id, name: m.name, phone: m.phone, email: m.email, note: m.note, active: m.active })}
            onDelete={() => window.confirm(`${m.name} silinsin mi?`) && run(() => api.delete(`/club/members/${m.id}`), "Üye silindi")}
            onAddPkg={() => setPkgDlg({ memberId: m.id, meals: 26, price: 4500, note: "" })}
            onDeletePkg={(p) => window.confirm("Paket ve ödemeleri silinsin mi?") && run(() => api.delete(`/club/members/${m.id}/packages/${p.id}`), "Paket silindi")}
            onMeal={(p, delta) => run(() => api.post(`/club/members/${m.id}/packages/${p.id}/meals`, { delta }),
              (r) => (delta < 0 ? "Öğün kullanıldı" : "Öğün iade edildi") + (r.data.stock_product ? ` · stoktan ${delta < 0 ? "düşüldü" : "iade edildi"}: ${r.data.stock_product}` : " · stok ürünü tanımlı değil"))}
            onPay={(p) => setPayDlg({ memberId: m.id, pkgId: p.id, amount: p.balance > 0 ? p.balance : "", payment_type: "Nakit", date: todayISO(), note: "", commission_amount: "", owner_name: "" })}
            onDeletePay={(p, pay) => window.confirm("Ödeme silinsin mi?") && run(() => api.delete(`/club/members/${m.id}/packages/${p.id}/payments/${pay.id}`), "Ödeme silindi")}
          />
        ))}
      </div>

      <Dialog open={!!memberDlg} onOpenChange={(o) => !o && setMemberDlg(null)}>
        <DialogContent data-testid="member-dialog">
          <DialogHeader><DialogTitle>{memberDlg?.id ? "Üyeyi Düzenle" : "Yeni Üye"}</DialogTitle></DialogHeader>
          {memberDlg && (
            <div className="space-y-3">
              {[["name", "Ad Soyad"], ["phone", "Telefon"], ["email", "E-posta"], ["note", "Not"]].map(([k, l]) => (
                <div key={k}><Label className="text-xs">{l}</Label>
                  <Input value={memberDlg[k]} data-testid={`member-${k}`} onChange={(e) => setMemberDlg({ ...memberDlg, [k]: e.target.value })} /></div>
              ))}
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={memberDlg.active} data-testid="member-active" onChange={(e) => setMemberDlg({ ...memberDlg, active: e.target.checked })} /> Aktif üye</label>
            </div>
          )}
          <DialogFooter><Button onClick={saveMember} data-testid="member-save">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pkgDlg} onOpenChange={(o) => !o && setPkgDlg(null)}>
        <DialogContent data-testid="package-dialog">
          <DialogHeader><DialogTitle>Öğün Paketi Tanımla</DialogTitle></DialogHeader>
          {pkgDlg && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Öğün sayısı</Label><Input type="number" min="1" value={pkgDlg.meals} data-testid="package-meals" onChange={(e) => setPkgDlg({ ...pkgDlg, meals: e.target.value })} /></div>
              <div><Label className="text-xs">Paket bedeli (₺)</Label><Input type="number" min="0" step="0.01" value={pkgDlg.price} data-testid="package-price" onChange={(e) => setPkgDlg({ ...pkgDlg, price: e.target.value })} /></div>
              <div className="col-span-2"><Label className="text-xs">Not</Label><Input value={pkgDlg.note} data-testid="package-note" onChange={(e) => setPkgDlg({ ...pkgDlg, note: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={savePkg} data-testid="package-save">Paketi Ekle</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payDlg} onOpenChange={(o) => !o && setPayDlg(null)}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle>Tahsilat Kaydet</DialogTitle></DialogHeader>
          {payDlg && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Tutar (₺)</Label><Input type="number" min="0" step="0.01" value={payDlg.amount} data-testid="payment-amount" onChange={(e) => setPayDlg({ ...payDlg, amount: e.target.value })} /></div>
              <div><Label className="text-xs">Ödeme türü</Label>
                <Select value={payDlg.payment_type} onValueChange={(v) => setPayDlg({ ...payDlg, payment_type: v })}>
                  <SelectTrigger data-testid="payment-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Nakit", "Kart", "Havale"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Tarih</Label><Input type="date" value={payDlg.date} max={todayISO()} data-testid="payment-date" onChange={(e) => setPayDlg({ ...payDlg, date: e.target.value })} /></div>
              <div><Label className="text-xs">Not</Label><Input value={payDlg.note} data-testid="payment-note" onChange={(e) => setPayDlg({ ...payDlg, note: e.target.value })} /></div>
              <div className="col-span-2 rounded-lg border border-dashed border-primary/40 bg-primary/5 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-primary">Kulüp sahibi payı</p>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Tutar (₺)</Label><Input type="number" min="0" step="0.01" placeholder="örn. 900" value={payDlg.commission_amount} data-testid="payment-commission-amount" onChange={(e) => setPayDlg({ ...payDlg, commission_amount: e.target.value })} /></div>
                  <div><Label className="text-xs">Kulüp sahibi (isteğe bağlı)</Label><Input placeholder="Ad" value={payDlg.owner_name} data-testid="payment-owner-name" onChange={(e) => setPayDlg({ ...payDlg, owner_name: e.target.value })} /></div>
                </div>
                <p className="mt-2 text-xs text-muted-foreground" data-testid="payment-commission-preview">
                  {toNum(payDlg.commission_amount) > 0
                    ? `Gider olarak yazılacak: ${formatTRY(toNum(payDlg.commission_amount))} · Kulüp net: ${formatTRY(toNum(payDlg.amount) - toNum(payDlg.commission_amount))}`
                    : "Tutar girilmezse pay yazılmaz"}
                </p>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={savePay} data-testid="payment-save">Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <FinanceDetailDialog detail={detail} onClose={() => setDetail(null)} onChanged={load} />
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, sub, warn }) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground"><span>{label}</span><Icon className={`h-4 w-4 ${warn ? "text-destructive" : "text-primary"}`} /></div>
    <p className={`mt-2 font-heading text-2xl font-extrabold ${warn ? "text-destructive" : ""}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
  </div>
);
